import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { onAuthStateChanged } from 'firebase/auth'
import { LogIn, MapPin, Sparkles, Ticket } from 'lucide-react'
import StampProgress from '../../components/StampProgress'
import StampSuccessAnimation from '../../components/StampSuccessAnimation'
import { auth } from '../../lib/firebase'
import { readArEvent } from '../../lib/arLaunch'
import { useI18n } from '../../i18n/useI18n'
import { api, type StampProgress as StampProgressData } from '../../services/api'
import CollectedCharacters from '../../components/CollectedCharacters'

const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect'

type Phase =
  | { kind: 'resolving' }
  | { kind: 'opened'; progress: StampProgressData } // 宝箱GET (1個以上開封済み)
  | { kind: 'none'; progress: StampProgressData }   // まだ何も開いていない
  | { kind: 'needs-login' }
  | { kind: 'error' }

/**
 * /stamp-rally/ar/return — WebAR からの復帰演出 (#1154)。
 *
 * スタンプ付与は WebAR が /stamp/ar/complete を直接呼んで確定済みなので、
 * この画面は**表示専用**: サーバの進捗を読み直して「宝箱GET! スタンプ+1」を
 * 見せるだけ。演出のために付与を二重実行しない。
 * 対象イベントは起動時に sessionStorage へ残した id (readArEvent)。
 */
export default function ArReturn() {
  const navigate = useNavigate()
  const { t } = useI18n()
  const [phase, setPhase] = useState<Phase>({ kind: 'resolving' })
  const fetchedRef = useRef(false) // StrictMode の二重 fetch 防止

  useEffect(() => {
    const eventId = readArEvent()
    if (!eventId) {
      // 起動履歴なしの直リンク — 判定を持つ TOP へ
      navigate('/stamp-rally', { replace: true })
      return
    }
    // WebAR からの復帰はフルロードのため Firebase auth の復元を待ってから叩く
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (fetchedRef.current) return
      if (!user) {
        setPhase({ kind: 'needs-login' })
        return
      }
      fetchedRef.current = true
      api
        .getStampProgress(eventId)
        .then(({ progress }) => {
          setPhase(progress.stampCount > 0 ? { kind: 'opened', progress } : { kind: 'none', progress })
        })
        .catch(() => setPhase({ kind: 'error' }))
    })
    return unsubscribe
  }, [navigate])

  const eventId = readArEvent()

  return (
    <div className="min-h-screen bg-warm-white flex flex-col items-center px-6 pt-14 pb-10 safe-top safe-bottom">
      {phase.kind === 'resolving' && (
        <div className="flex flex-col items-center justify-center flex-1 gap-4">
          <div className="h-8 w-8 rounded-full border-2 border-gray-300 border-t-yokoso-red animate-spin" />
          <p className="font-body text-sm text-text-secondary">{t('stampRally.arReturnLoading')}</p>
        </div>
      )}

      {phase.kind === 'needs-login' && (
        <div className="flex flex-col items-center justify-center flex-1 gap-4 text-center">
          <LogIn size={56} className="text-navy" />
          <p className="font-body text-sm text-text-secondary">{t('stampRally.loginNote')}</p>
          <button
            onClick={() => {
              sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, '/stamp-rally/ar/return')
              navigate('/login')
            }}
            className="rounded-full bg-yokoso-red text-white px-8 py-3 text-sm font-body font-medium tap-effect"
          >
            {t('stampRally.loginToStamp')}
          </button>
        </div>
      )}

      {phase.kind === 'error' && (
        <div className="flex flex-col items-center justify-center flex-1 gap-4 text-center">
          <MapPin size={48} className="text-yokoso-red" />
          <p className="font-body text-text-primary">{t('stampRally.scanError')}</p>
        </div>
      )}

      {(phase.kind === 'opened' || phase.kind === 'none') && (
        <>
          {phase.kind === 'opened' ? (
            <>
              <StampSuccessAnimation />
              <p className="mt-2 font-display text-xl text-navy">{t('stampRally.arChestGot')}</p>
            </>
          ) : (
            <div className="flex flex-col items-center animate-fade-in text-center">
              <Sparkles size={48} className="text-text-tertiary mb-3" />
              <p className="font-body font-medium text-text-primary">{t('stampRally.arNoneOpened')}</p>
              <p className="text-xs font-body text-text-tertiary mt-1">{t('stampRally.arNoneOpenedHint')}</p>
            </div>
          )}

          {eventId && (
            <div className="w-full mt-6">
              <CollectedCharacters eventId={eventId} />
            </div>
          )}

          <div className="w-full mt-6">
            <StampProgress
              count={phase.progress.stampCount}
              goal={phase.progress.goal}
              justEarned={phase.kind === 'opened'}
            />
            {phase.progress.achieved ? (
              <div className="mt-3 bg-gold/10 rounded-xl px-4 py-3 text-center animate-fade-in">
                <p className="text-sm font-body font-medium text-text-primary mb-2">
                  {t('stampRally.achievedBanner')}
                </p>
                <button
                  onClick={() =>
                    navigate(`/stamp-rally/ticket?eventId=${encodeURIComponent(eventId ?? '')}`)
                  }
                  className="inline-flex items-center gap-2 rounded-full bg-gold text-white px-6 py-2.5 text-sm font-body font-medium tap-effect"
                >
                  <Ticket size={16} />
                  {t('stampRally.goToTicket')}
                </button>
              </div>
            ) : (
              phase.kind === 'opened' && (
                <p className="mt-3 text-center text-sm font-body text-text-secondary">
                  {t('stampRally.oneMore', {
                    remaining: String(Math.max(0, phase.progress.goal - phase.progress.stampCount)),
                  })}
                </p>
              )
            )}
          </div>
        </>
      )}

      {phase.kind !== 'resolving' && (
        <button
          onClick={() => navigate('/stamp-rally')}
          className="mt-auto pt-6 text-center text-sm font-body text-text-secondary tap-effect py-2"
        >
          {t('stampRally.backToTop')}
        </button>
      )}
    </div>
  )
}
